import { useGSAP } from '@gsap/react';
import { format } from 'date-fns';
import gsap from 'gsap';
import { t } from 'i18next';
import React, { useRef } from 'react';
import { Experience } from '../../../templates/ExperiencePage';
import { areSameDay } from '../../../utils/projectUtils';
import { CMSPlusAPIExperience } from './ExperiencesBookingModule';
import { clsx, urlJoin } from '../../../utils/utils';
import ButtonLink from '../ButtonLink';
import Image from '../Image';
import ModuleLayout from '../ModuleLayout';
import PriceTag from '../PriceTag';
import * as styles from './ExperiencesModule.module.scss';

export type ExperienceWithSoldOutStatus = Experience & {
  soldOutStatus?: 'soldOutForDate' | 'soldOutForSeason';
};

export type ExperiencesModuleProps = {
  experiences: Array<CMSPlusAPIExperience | Experience>;
  selectedDate: Date | null;
  loading?: boolean;
  buttonText?: string;
  className?: string;
};

function ExperiencesModule(props: ExperiencesModuleProps): React.ReactElement {
  const { experiences, loading, selectedDate, buttonText, className } = props;
  const containerRef = useRef<HTMLElement | null>(null);

  useGSAP(
    () => {
      const cards = gsap.utils.toArray<HTMLDivElement>(
        `.${styles.experienceContainer}, .${styles.loadingExperience}`,
      );

      cards.forEach(card => {
        // Durations are used to control how much scroll time is
        // spent on entry, middle and exit
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: card,
            start: 'top 130%',
            end: 'bottom -10%',
            scrub: 1,
          },
        });
        tl.from(card, {
          scale: 0.8,
          y: 100,
          duration: 1,
          ease: 'power1.out',
        });
        tl.to(card, {
          scale: 0.8,
          y: -100,
          delay: 1,
          duration: 1,
          ease: 'power1.in',
        });
      });
    },
    { scope: containerRef, dependencies: [loading] },
  );

  function renderExperience(experience: CMSPlusAPIExperience | Experience, loading?: boolean) {
    if (!loading) {
      const experienceHasAPIData = 'dates' in experience;
      const soldOutForSeason = experienceHasAPIData && experience.dates.length === 0;
      const apiDateForSelectedDate =
        selectedDate &&
        experienceHasAPIData &&
        experience.dates.find(date => areSameDay(new Date(date.date.split('T')[0]), selectedDate));
      const soldOutForDate = !!(selectedDate && experienceHasAPIData && !apiDateForSelectedDate);
      const soldOut = soldOutForSeason || soldOutForDate;

      return (
        <div className={styles.experienceContainer} data-theme={experience.theme}>
          <div className={styles.imageContainer}>
            <Image
              image={experience.image}
              className={styles.image}
              width={480}
              height={384}
              cover
            />
            {'price' in experience && (
              <PriceTag price={experience.price} className={styles.priceTag} />
            )}
          </div>
          <div className={styles.textContainer}>
            {((selectedDate && soldOut) || soldOutForSeason) && (
              <p className={styles.soldOutTag}>
                {soldOutForSeason
                  ? t('experience.coming_soon', 'Coming soon')
                  : soldOutForDate
                    ? t('experience.sold_out_for_date', 'Sold out for {{selectedDate}}', {
                        selectedDate: format(selectedDate, 'MMMM do'),
                      })
                    : null}
              </p>
            )}
            <div className={styles.textWrapper}>
              <h2 className={styles.experienceTitle}>{experience.title}</h2>
              <p className={styles.experienceDescription}>{experience.description}</p>
              <ButtonLink
                secondary={!!soldOut}
                className={styles.experienceButton}
                button={{
                  text: soldOut
                    ? t('experience.button_view_experience', 'View experience')
                    : buttonText || t('experience.button_reserve_yout_seat', 'Reserve your seat'),
                  linkType: 'internalLink',
                  internalLink: {
                    pageReference: {
                      __typename: 'SanityExperience',
                      slug: {
                        current: urlJoin(
                          experience.slug.current,
                          selectedDate && apiDateForSelectedDate
                            ? `?date=${format(selectedDate, 'yyyy-MM-dd')}&dateId=${
                                apiDateForSelectedDate.id
                              }`
                            : '',
                        ),
                      },
                    },
                  },
                }}
              />
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div
          className={clsx(styles.experienceContainer, styles.loadingExperience)}
          data-theme={experience.theme}
        >
          <div className={styles.spinner}>
            <svg viewBox="25 25 50 50">
              <circle cx="50" cy="50" r="20" fill="none" className={styles.path}></circle>
            </svg>
          </div>
        </div>
      );
    }
  }

  return (
    <ModuleLayout
      ref={containerRef}
      className={className}
      childrenClassName={styles.contentContainer}
    >
      {selectedDate && (
        <p className={styles.currentDateLabel}>
          {t(
            'experiences_page.showing_experiences_label',
            'Showing Experiences on {{selectedDate}}:',
            { selectedDate: format(selectedDate, 'MMMM do') },
          )}
        </p>
      )}
      <div className={styles.experiencesContainer}>
        {experiences.map((experience, i) => (
          <div className={styles.experienceStableWrapper} data-theme={experience.theme} key={i}>
            {renderExperience(experience, loading)}
          </div>
        ))}
      </div>
    </ModuleLayout>
  );
}

export default ExperiencesModule;
